import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyMenuModule, MatLegacyMenuTrigger } from '@angular/material/legacy-menu';
import { DataRole } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { DomainResult } from '@mode-switch/query';
import { EncDiscreteBooleanFilterField, EncDiscreteFilterField, SelectionMode } from '@mode-vegas/vegas';
import { ClickOutsideDirective, IconComponent, TooltipDirective } from '@mode/capra';
import { ReportTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import { MultiSelectComponent } from '../multi-select/multi-select.component';
import { SingleSelectComponent } from './../single-select/single-select.component';

@Component({
  selector: 'mode-dropdown-filter',
  standalone: true,
  imports: [
    CommonModule,
    MultiSelectComponent,
    SingleSelectComponent,
    IconComponent,
    TooltipDirective,
    MatLegacyButtonModule,
    MatLegacyMenuModule,
    ClickOutsideDirective,
  ],
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
})
export class DropdownComponent implements OnChanges {
  @Input() filter!: EncDiscreteFilterField | EncDiscreteBooleanFilterField;
  @Input() filterOption!: DomainResult<DataRole>;
  @Input() compiledField: CompiledField | null = null;
  @Input() isLoading = true;
  @Input() showAll = true;
  @Input() isMulti = true;
  @Input() isSingle = false;

  @ViewChild(CdkVirtualScrollViewport, { static: false })
  cdkVirtualScrollViewPort: CdkVirtualScrollViewport | undefined;

  @ViewChild('singleMenuTrigger')
  singleMenuTrigger: MatLegacyMenuTrigger | undefined;

  @ViewChild('multiMenuTrigger')
  multiMenuTrigger: MatLegacyMenuTrigger | undefined;

  dropdownLabel: string | undefined;
  isOpen = false;
  optionsSet: Set<unknown> = new Set();

  @Output() multiSelectChange = new EventEmitter<{
    filter: EncDiscreteFilterField | EncDiscreteBooleanFilterField;
    selectedValue: ReportTypes.ReportFilterValue;
    isChecked: boolean;
    searchTerm: string;
  }>();

  @Output() singleSelectChange = new EventEmitter<EncDiscreteFilterField | EncDiscreteBooleanFilterField>();

  onMultiSelectChange(
    filter: EncDiscreteFilterField | EncDiscreteBooleanFilterField,
    selectedValue: ReportTypes.ReportFilterValue,
    isChecked: boolean,
    searchTerm: string
  ) {
    this.filter = filter;
    this.multiSelectChange.emit({ filter, selectedValue, isChecked, searchTerm });
    this.setDropdownLabelText(filter);
  }

  onSingleSelectChange(filter: EncDiscreteFilterField | EncDiscreteBooleanFilterField) {
    this.filter = filter;
    this.singleSelectChange.emit(filter);
    this.setDropdownLabelText(filter);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterOption?.currentValue) {
      if (this.filterOption?.type === Attr.Type.Discrete) {
        this.optionsSet = new Set(this.filterOption.values ?? []);
      }
    }

    if (changes.filterOption?.currentValue || changes.filter?.currentValue) {
      this.setDropdownLabelText(this.filter);
    }
  }

  setDropdownLabelText(filter: EncDiscreteFilterField | EncDiscreteBooleanFilterField) {
    const selectionCondition = filter.selection;
    const selectedValues: unknown[] = [];
    selectionCondition.selected.forEach((value) => {
      if (this.optionsSet.has(value)) {
        selectedValues.push(value);
      }
    });
    if (filter.selection.mode === SelectionMode.ALL) {
      this.dropdownLabel = 'All selected';
    } else if (selectedValues.length > 0) {
      const firstSelectedVal = selectedValues[0];

      // single-select OR only 1 item is selected in multi-select
      if (this.isSingle || selectedValues.length === 1) {
        this.dropdownLabel = `${firstSelectedVal}`;
      } else {
        this.dropdownLabel = `${firstSelectedVal} + ${selectedValues.length - 1} more selected`;
      }
    } else {
      this.dropdownLabel = 'Nothing selected';
    }
  }

  closeDropdown() {
    if (this.isOpen) {
      this.multiMenuTrigger?.closeMenu();
      this.singleMenuTrigger?.closeMenu();
    }
  }
}
